<template>
  <div class="vendors-list-container vendors-list-main vendors-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Vendors']" />

    <div class="glass-tab-view glass-tab-view--flush vendors-list-glass-shell">
      <div class="glass-tab-view__content vendors-list-tab-content vendors-list-page-stack">
        <div v-if="!selectedVendor" class="vendors-list-toolbar vendors-list-toolbar-glass vendors-list-toolbar-row glass-tab-panel--nested">
          <div class="vendors-list-filters vendors-list-filters-row">
            <div class="vendors-list-filter-field vendors-list-filter-field--view">
              <LabelDropdown
                label="Current View"
                select-id="vendors-list-current-view"
                v-model="currentView"
                inline
                flex
                menu-class="vendors-list-dropdown-menu"
                :options="currentViewOptions"
              />
            </div>
            <div class="vendors-list-filter-field vendors-list-filter-field--role">
              <LabelDropdown
                label="Vendor Type"
                select-id="vendors-list-vendor-role"
                v-model="vendorRoleFilter"
                inline
                flex
                searchable
                menu-class="vendors-list-dropdown-menu"
                :options="vendorRoleFilterOptions"
              />
            </div>
            <div class="vendors-list-filter-field vendors-list-filter-field--search">
              <div class="vendors-list-search">
                <span class="vendors-list-search__label">Search</span>
                <SearchComponent
                  :search-query="searchQuery"
                  placeholder="Search vendor..."
                  @search="onSearchInput"
                />
              </div>
            </div>
          </div>
          <div class="vendors-list-header-actions">
            <ViewToggle v-model="viewMode" />
            <SecondaryButton
              label="Export"
              icon="bi bi-download"
              @click="showExportModal = true"
            />
            <SecondaryButton
              label="Import"
              icon="bi bi-upload"
              @click="navigateToImport"
            />
            <PrimaryButton
              label="Add New Vendor"
              icon="bi bi-plus-lg"
              @click="navigateToAddNewVendor"
            />
          </div>
        </div>

        <div class="vendors-list-split-view">
          <div v-if="!selectedVendor" class="vendors-list-left-panel">
            <div class="vendors-list-glass-panel vendors-list-main-card">
              <div v-if="viewMode === 'table'" class="consultant-glass-table-section vendors-list-table-section">
                <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                  <div class="consultant-glass-table-scroll-x">
                    <table class="consultant-glass-table vendors-list-table">
                      <thead>
                        <tr>
                          <th>Active</th>
                          <th>Vendor ID</th>
                          <th>Vendor Name</th>
                          <th>January</th>
                          <th>February</th>
                          <th>March</th>
                          <th>April</th>
                          <th>May</th>
                          <th>June</th>
                          <th>July</th>
                          <th>August</th>
                          <th>September</th>
                          <th>October</th>
                          <th>November</th>
                          <th>December</th>
                          <th>Year to Date</th>
                          <th>Balance</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="loading && paginatedVendors.length === 0">
                          <td colspan="18" class="consultant-glass-table-no-records vendors-list-no-records">
                            <span v-if="loadingMoreVendors && vendors.length > 0">
                              Loading vendors... {{ vendors.length.toLocaleString() }}{{ totalVendorsCount > 0 ? ` / ${totalVendorsCount.toLocaleString()}` : '' }} loaded
                            </span>
                            <span v-else>Loading vendors...</span>
                          </td>
                        </tr>
                        <tr v-else-if="loading && paginatedVendors.length > 0">
                          <td colspan="18" class="consultant-glass-table-no-records vendors-list-no-records">
                            Loading more vendors... {{ vendors.length.toLocaleString() }}{{ totalVendorsCount > 0 ? ` / ${totalVendorsCount.toLocaleString()}` : '' }}
                          </td>
                        </tr>
                        <tr v-else-if="error">
                          <td colspan="18" class="consultant-glass-table-no-records vendors-list-no-records">{{ error }}</td>
                        </tr>
                        <tr v-else-if="paginatedVendors.length === 0">
                          <td colspan="18" class="consultant-glass-table-no-records vendors-list-no-records">No records to show</td>
                        </tr>
                        <template v-else>
                          <tr
                            v-for="vendor in paginatedVendors"
                            :key="vendor.id"
                            class="consultant-glass-table-row vendors-list-row"
                            @click="showVendorPreview(vendor)"
                          >
                            <td><input type="checkbox" :checked="vendor.active !== false" disabled /></td>
                            <td>{{ vendor.vendorId || vendor.id || '-' }}</td>
                            <td>{{ vendor.vendorName || vendor.name }}</td>
                            <td
                              v-for="month in monthColumns"
                              :key="month"
                              class="vendors-list-amount-cell"
                              :class="{ 'vendors-list-amount-cell--empty': !getVendorMonthlyAmount(vendor.id, month) }"
                            >{{ formatCurrency(getVendorMonthlyAmount(vendor.id, month)) }}</td>
                            <td
                              class="vendors-list-amount-cell"
                              :class="{ 'vendors-list-amount-cell--empty': !getVendorYearToDate(vendor.id) }"
                            >{{ formatCurrency(getVendorYearToDate(vendor.id)) }}</td>
                            <td
                              class="vendors-list-amount-cell"
                              :class="{ 'vendors-list-amount-cell--empty': vendor.balance == null }"
                            >{{ vendor.balance != null ? ('$' + Number(vendor.balance).toFixed(2)) : '-' }}</td>
                            <td class="vendors-list-actions">
                              <button
                                type="button"
                                class="vendors-list-edit-btn"
                                title="Edit Vendor"
                                @click.stop="editVendor(vendor.id)"
                              >
                                <i class="bi bi-pencil-square"></i>
                              </button>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div v-else class="vendors-list-grid-wrap">
                <p v-if="loading && paginatedVendors.length === 0" class="vendors-list-grid-empty">
                  <span v-if="loadingMoreVendors && vendors.length > 0">
                    Loading vendors... {{ vendors.length.toLocaleString() }}{{ totalVendorsCount > 0 ? ` / ${totalVendorsCount.toLocaleString()}` : '' }} loaded
                  </span>
                  <span v-else>Loading vendors...</span>
                </p>
                <p v-else-if="error" class="vendors-list-grid-empty">{{ error }}</p>
                <p v-else-if="paginatedVendors.length === 0" class="vendors-list-grid-empty">No records to show</p>
                <div v-else class="vendors-list-grid">
                  <article
                    v-for="vendor in paginatedVendors"
                    :key="'grid-' + vendor.id"
                    class="vendors-list-card"
                    role="button"
                    tabindex="0"
                    @click="showVendorPreview(vendor)"
                    @keydown.enter.prevent="showVendorPreview(vendor)"
                    @keydown.space.prevent="showVendorPreview(vendor)"
                  >
                    <header class="vendors-list-card__header">
                      <div
                        class="vendors-list-card__avatar"
                        :class="{ 'vendors-list-card__avatar--image': Boolean(vendorLogoUrl(vendor)) }"
                        aria-hidden="true"
                      >
                        <img
                          v-if="vendorLogoUrl(vendor)"
                          :src="vendorLogoUrl(vendor)"
                          alt=""
                          class="vendors-list-card__avatar-img"
                          @error="onVendorLogoError(vendor)"
                        >
                        <template v-else>{{ vendorInitials(vendor) }}</template>
                      </div>
                      <div class="vendors-list-card__heading">
                        <h3 class="vendors-list-card__name" :title="vendor.vendorName || vendor.name || ''">
                          {{ vendor.vendorName || vendor.name || '—' }}
                        </h3>
                        <p class="vendors-list-card__id">
                          {{ vendor.vendorId || vendor.id || '—' }}
                        </p>
                      </div>
                      <span
                        class="vendors-list-card__status"
                        :class="vendor.active !== false ? 'vendors-list-card__status--active' : 'vendors-list-card__status--inactive'"
                      >
                        <span class="vendors-list-card__status-dot" aria-hidden="true" />
                        {{ vendor.active !== false ? 'Active' : 'Inactive' }}
                      </span>
                    </header>

                    <div class="vendors-list-card__chips">
                      <span class="vendors-list-card__chip vendors-list-card__chip--role">
                        <i class="bi bi-briefcase" aria-hidden="true" />
                        {{ vendorRoleLabel(vendor.vendorRole) || 'Type not set' }}
                      </span>
                    </div>

                    <div class="vendors-list-card__stats" aria-label="Vendor summary">
                      <div class="vendors-list-card__stat">
                        <span class="vendors-list-card__stat-label">Balance</span>
                        <strong class="vendors-list-card__stat-value vendors-list-card__stat-value--balance">
                          {{ vendor.balance != null ? ('$' + Number(vendor.balance).toFixed(2)) : '—' }}
                        </strong>
                      </div>
                      <div class="vendors-list-card__stat">
                        <span class="vendors-list-card__stat-label">Portal</span>
                        <strong
                          class="vendors-list-card__stat-value"
                          :class="portalStatusClass(vendor)"
                        >
                          {{ portalStatusLabel(vendor) }}
                        </strong>
                      </div>
                    </div>

                    <ul class="vendors-list-card__meta">
                      <li>
                        <i class="bi bi-envelope" aria-hidden="true" />
                        <span class="vendors-list-card__meta-label">Email</span>
                        <span
                          class="vendors-list-card__meta-value"
                          :title="vendor.email || ''"
                        >{{ vendor.email || 'No email' }}</span>
                      </li>
                      <li>
                        <i class="bi bi-telephone" aria-hidden="true" />
                        <span class="vendors-list-card__meta-label">Phone</span>
                        <span class="vendors-list-card__meta-value">{{ vendor.phone || 'No phone' }}</span>
                      </li>
                    </ul>

                    <footer class="vendors-list-card__actions">
                      <PrimaryButton
                        label="View"
                        icon="bi bi-eye"
                        compact
                        custom-class="vendors-list-card__view-btn"
                        @click.stop="showVendorPreview(vendor)"
                      />
                      <SecondaryButton
                        :label="`Consultants: ${consultantCountForVendor(vendor)}`"
                        icon="bi bi-people"
                        title="View consultants for this vendor"
                        custom-class="vendors-list-card__consultants-btn"
                        @click.stop="openVendorConsultants(vendor)"
                      />
                      <SecondaryButton
                        label=""
                        icon="bi bi-pencil-square"
                        title="Edit Vendor"
                        custom-class="vendors-list-card__icon-btn"
                        @click.stop="editVendor(vendor.id)"
                      />
                    </footer>
                  </article>
                </div>
              </div>

              <div class="vendors-list-pagination-wrapper">
                  <PaginatorComponent
                    v-if="filteredVendors.length >= 2000"
                    :current-page="currentPage"
                    :total-count="filteredVendors.length"
                    :records-per-page="itemsPerPage"
                    :loading="loadingMoreVendors"
                    @page-changed="handlePageChange"
                  />
                  <PaginationComponent
                    v-else
                    select-id="vendors-list-items-per-page"
                    :current-page="currentPage"
                    :total-items="filteredVendors.length"
                    :items-per-page="itemsPerPage"
                    item-label="vendors"
                    @page-change="handlePageChange"
                    @items-per-page-change="handleItemsPerPageChange"
                  />
              </div>
            </div>
          </div>

          <div v-if="selectedVendor" class="vendors-list-preview-panel vendors-list-preview-full-width">
            <div class="vendors-list-preview-content glass-tab-panel--nested">
              <VendorPreviewContent
                :vendor="selectedVendor"
                @edit="editSelectedVendor"
                @close="closePreview"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ExportModal
      :show="showExportModal"
      title="Export Vendors"
      description="Choose your preferred export format to download the vendors data"
      @close="showExportModal = false"
      @export-excel="exportToExcel"
    />
    <VendorDetailsSearch v-if="showSearchModal" @close="showSearchModal = false" />
  </div>
</template>

<script>
import '../styles/VendorsList.css';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import PaginatorComponent from '@/components/PaginatorComponent.vue';
import VendorPreviewContent from '../components/VendorPreviewContent.vue';
import ExportModal from '@/modules/consultant/components/ExportModal.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import ViewToggle from '@/components/ViewToggle.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import { VENDOR_ROLE_FILTER_OPTIONS, getVendorRoleLabel } from '@/constants/vendorRoles.js';
import { exportVendorsToExcel } from '../utils/vendorExportFile.js';
export default {
  name: 'VendorsList',
  components: {
    VendorDetailsSearch,
    BrucdcumHeader,
    PaginationComponent,
    PaginatorComponent,
    VendorPreviewContent,
    ExportModal,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
    ViewToggle,
    SearchComponent,
  },
  data() {
    return {
      monthColumns: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
      vendors: [],
      bills: [],
      loading: false,
      error: '',
      currentView: 'All',
      vendorRoleFilter: '',
      searchQuery: '',
      debouncedSearchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      showSearchModal: false,
      selectedVendor: null,
      viewMode: (typeof localStorage !== 'undefined' && localStorage.getItem('vendorsListViewMode') === 'grid') ? 'grid' : 'table',
      showExportModal: false,
      debounceTimer: null,
      currentRequestController: null,
      computedCache: {
        filteredVendors: null,
        lastFilterHash: null,
        filteredVendorsTimestamp: null,
        cacheMaxAge: 60000,
      },
      vendorsPage: 1,
      vendorsPerPage: 1000,
      totalVendorsCount: 0,
      hasMoreVendors: true,
      loadingMoreVendors: false,
      requestQueue: [],
      isProcessingQueue: false,
      maxConcurrentRequests: 2,
      activeRequests: 0,
      requestDelay: 100,
      maxRetries: 3,
      retryDelay: 1000,
      vendorLogoUrls: {},
      consultantCountsByVendor: {},
    };
  },
  async created() {
    if (String(this.$route.query.view || '').toLowerCase() === 'grid') {
      this.viewMode = 'grid';
    }
    await this.fetchVendorsAndBills();
    this.loadConsultantCounts();
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId || '';
    },
    currentViewOptions() {
      return [
        { value: 'All', label: 'All' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ];
    },
    vendorRoleFilterOptions() {
      return VENDOR_ROLE_FILTER_OPTIONS;
    },
    filteredVendors() {
      const filterHash = JSON.stringify({
        vendorsLength: this.vendors.length,
        currentView: this.currentView,
        vendorRoleFilter: this.vendorRoleFilter,
        debouncedSearchQuery: this.debouncedSearchQuery,
      });

      const currentTime = Date.now();
      const cacheAge = this.computedCache.filteredVendorsTimestamp
        ? currentTime - this.computedCache.filteredVendorsTimestamp
        : Infinity;
      const isCacheValid = this.computedCache.filteredVendors
        && this.computedCache.lastFilterHash === filterHash
        && cacheAge < this.computedCache.cacheMaxAge;

      if (isCacheValid) {
        return this.computedCache.filteredVendors;
      }

      let filtered = this.vendors;
      if (this.currentView === 'Active') {
        filtered = filtered.filter((v) => v.active !== false);
      } else if (this.currentView === 'Inactive') {
        filtered = filtered.filter((v) => v.active === false);
      }

      if (this.vendorRoleFilter) {
        filtered = filtered.filter((v) => v.vendorRole === this.vendorRoleFilter);
      }

      if (this.debouncedSearchQuery) {
        const q = this.debouncedSearchQuery.toLowerCase().trim();
        filtered = filtered.filter((v) => {
          const vendorName = (v.vendorName || v.name || '').toLowerCase();
          const vendorId = (v.vendorId || '').toLowerCase();
          const email = (v.email || '').toLowerCase();
          return vendorName.includes(q) || vendorId.includes(q) || email.includes(q);
        });
      }

      return filtered;
    },
    paginatedVendors() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredVendors.slice(start, start + this.itemsPerPage);
    },
  },
  watch: {
    searchQuery() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = this.searchQuery;
        this.currentPage = 1;
        this.computedCache.filteredVendors = null;
        this.computedCache.lastFilterHash = null;
        this.computedCache.filteredVendorsTimestamp = null;
      }, 300);
    },
    currentView() {
      this.currentPage = 1;
      this.computedCache.filteredVendors = null;
      this.computedCache.lastFilterHash = null;
      this.computedCache.filteredVendorsTimestamp = null;
      this.fetchVendorsAndBills();
    },
    vendorRoleFilter() {
      this.currentPage = 1;
      this.computedCache.filteredVendors = null;
      this.computedCache.lastFilterHash = null;
      this.computedCache.filteredVendorsTimestamp = null;
      this.fetchVendorsAndBills();
    },
    vendors: {
      handler() {
        this.computedCache.filteredVendors = null;
        this.computedCache.lastFilterHash = null;
        this.computedCache.filteredVendorsTimestamp = null;
      },
      deep: true,
    },
    filteredVendors: {
      handler(newVal) {
        if (newVal) {
          const filterHash = JSON.stringify({
            vendorsLength: this.vendors.length,
            currentView: this.currentView,
            vendorRoleFilter: this.vendorRoleFilter,
            debouncedSearchQuery: this.debouncedSearchQuery,
          });
          this.computedCache.filteredVendors = newVal;
          this.computedCache.lastFilterHash = filterHash;
          this.computedCache.filteredVendorsTimestamp = Date.now();
        }
      },
      immediate: false,
    },
    viewMode(mode) {
      try {
        localStorage.setItem('vendorsListViewMode', mode === 'grid' ? 'grid' : 'table');
      } catch (_) {
        /* ignore */
      }
      if (mode === 'grid') {
        this.prefetchVendorLogosForVendors(this.paginatedVendors);
      }
    },
    paginatedVendors: {
      handler(list) {
        if (this.viewMode === 'grid') {
          this.prefetchVendorLogosForVendors(list);
        }
      },
      immediate: true,
    },
  },
  methods: {
    vendorInitials(vendor) {
      const name = String(vendor?.vendorName || vendor?.name || '').trim();
      if (!name) return '?';
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    },
    vendorLogoUrl(vendor) {
      const id = vendor?.id;
      if (id == null) return '';
      const cached = this.vendorLogoUrls[String(id)];
      return typeof cached === 'string' && cached ? cached : '';
    },
    vendorHasPortalLogo(vendor) {
      return Boolean(String(vendor?.portalLogo || '').trim());
    },
    onVendorLogoError(vendor) {
      const key = vendor?.id != null ? String(vendor.id) : '';
      if (!key) return;
      const url = this.vendorLogoUrls[key];
      if (url && String(url).startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
      this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
    },
    revokeAllVendorLogoUrls() {
      Object.values(this.vendorLogoUrls || {}).forEach((url) => {
        if (url && String(url).startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      this.vendorLogoUrls = {};
    },
    async prefetchVendorLogosForVendors(vendors = []) {
      const ids = [
        ...new Set(
          (vendors || [])
            .filter((v) => this.vendorHasPortalLogo(v))
            .map((v) => v?.id)
            .filter((id) => id != null)
            .map((id) => String(id))
        ),
      ];
      if (!ids.length) return;
      await Promise.all(ids.map((id) => this.ensureVendorLogoLoaded(id)));
    },
    async ensureVendorLogoLoaded(vendorId) {
      const key = String(vendorId);
      if (Object.prototype.hasOwnProperty.call(this.vendorLogoUrls, key)) return;
      if (!this.companyId || !key) {
        this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
        return;
      }

      this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: undefined };

      try {
        const response = await ApiService.get(
          `/vendor/vendors/${vendorId}/portal-logo?companyId=${encodeURIComponent(this.companyId)}`,
          { responseType: 'blob' }
        );
        const blob = response.data;
        const headerType = String(
          response.headers?.['content-type']
          || response.headers?.['Content-Type']
          || ''
        ).toLowerCase();
        const blobType = String(blob?.type || '').toLowerCase();
        const looksLikeImage = Boolean(
          blob instanceof Blob
          && blob.size > 0
          && !headerType.includes('json')
          && !blobType.includes('json')
          && (
            headerType.startsWith('image/')
            || blobType.startsWith('image/')
            || headerType.includes('octet-stream')
            || (!headerType && !blobType)
          )
        );
        if (!looksLikeImage) {
          this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
          return;
        }
        const mime = blobType.startsWith('image/')
          ? blobType
          : (headerType.startsWith('image/') ? headerType : 'image/jpeg');
        const url = URL.createObjectURL(
          blobType.startsWith('image/') ? blob : new Blob([blob], { type: mime })
        );
        this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: url };
      } catch (error) {
        logger.warn('Could not load vendor portal logo:', vendorId, error);
        this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
      }
    },
    vendorRoleLabel(role) {
      return getVendorRoleLabel(role) || '';
    },
    portalStatusLabel(vendor) {
      const status = String(vendor?.portalInviteStatus || 'none').toLowerCase();
      if (status === 'active') return 'Active';
      if (status === 'invited') return 'Invited';
      if (status === 'revoked') return 'Revoked';
      return 'Not invited';
    },
    portalStatusClass(vendor) {
      const status = String(vendor?.portalInviteStatus || 'none').toLowerCase();
      if (status === 'active') return 'vendors-list-card__stat-value--portal-active';
      if (status === 'invited') return 'vendors-list-card__stat-value--portal-invited';
      if (status === 'revoked') return 'vendors-list-card__stat-value--portal-revoked';
      return 'vendors-list-card__stat-value--portal-none';
    },
    consultantCountForVendor(vendor) {
      const id = vendor?.id;
      if (id == null) return 0;
      const count = this.consultantCountsByVendor[String(id)];
      return Number.isFinite(Number(count)) ? Number(count) : 0;
    },
    async loadConsultantCounts() {
      const companyId = this.companyId;
      if (!companyId) {
        this.consultantCountsByVendor = {};
        return;
      }
      try {
        const counts = await ApiService.getConsultantCountsByVendor(companyId);
        this.consultantCountsByVendor = counts && typeof counts === 'object' ? counts : {};
      } catch (error) {
        logger.warn('Could not load consultant counts by vendor:', error);
        this.consultantCountsByVendor = {};
      }
    },
    openVendorConsultants(vendor) {
      if (!vendor?.id) return;
      const companyId = this.companyId;
      if (!companyId) return;
      this.$router.push({
        name: 'ConsultantsList',
        params: { id: companyId },
        query: {
          vendorId: String(vendor.id),
          vendorName: vendor.vendorName || vendor.name || '',
          view: 'grid',
        },
      });
    },
    onSearchInput(value) {
      this.searchQuery = value == null ? '' : String(value);
    },
    async fetchVendorsAndBills() {
      this.cancelPendingRequest();
      this.currentRequestController = new AbortController();
      this.loading = true;
      this.error = '';
      this.revokeAllVendorLogoUrls();
      this.vendors = [];
      this.vendorsPage = 1;
      this.hasMoreVendors = true;
      this.totalVendorsCount = 0;

      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        logger.log('Company ID for vendors:', companyId);

        const billsResponse = await ApiService.get(`/vendor/enter-bill/company/${companyId}/bills-expenses`).catch(() => ({ data: [] }));

        let billsData = [];
        if (billsResponse && billsResponse.data) {
          billsData = Array.isArray(billsResponse.data) ? billsResponse.data : [];
        } else if (Array.isArray(billsResponse)) {
          billsData = billsResponse;
        } else if (billsResponse && typeof billsResponse === 'object') {
          billsData = [];
        }

        this.bills = billsData.filter((bill) => bill.type === 'Bill' || !bill.type);
        logger.log('Bills loaded:', this.bills.length);

        await this.loadAllVendors(companyId);
        this.calculateMonthlyData();
        logger.log('Vendors loaded:', this.vendors.length);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        logger.error('Error loading vendors:', err);
        this.error = 'Failed to load vendors.';
        this.vendors = [];
        this.bills = [];
      } finally {
        this.loading = false;
        this.currentRequestController = null;
      }
    },
    async loadAllVendors(companyId) {
      let maxPages = 10000;
      let pageCount = 0;
      let consecutiveErrors = 0;
      const maxConsecutiveErrors = 3;

      while (this.hasMoreVendors && pageCount < maxPages) {
        try {
          this.loadingMoreVendors = true;
          pageCount++;

          while (this.activeRequests >= this.maxConcurrentRequests) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }

          const params = new URLSearchParams({
            companyId,
            page: this.vendorsPage.toString(),
            limit: this.vendorsPerPage.toString(),
          });

          if (this.currentView === 'Active') {
            params.append('active', 'true');
          } else if (this.currentView === 'Inactive') {
            params.append('active', 'false');
          }

          if (this.vendorRoleFilter) {
            params.append('vendorRole', this.vendorRoleFilter);
          }

          logger.log(`Loading vendors page ${this.vendorsPage} (${this.vendors.length} loaded so far)...`);

          const response = await this.makeRequestWithRetry(
            `/vendor/vendors?${params.toString()}`,
            { signal: this.currentRequestController.signal },
          );

          consecutiveErrors = 0;

          let newVendors = [];
          let paginationInfo = null;

          if (response.data && response.data.success && Array.isArray(response.data.data)) {
            newVendors = response.data.data;
            paginationInfo = response.data.pagination;
            this.totalVendorsCount = paginationInfo?.total || 0;
            const receivedFullPage = newVendors.length === this.vendorsPerPage;
            const hasMoreFlag = paginationInfo?.hasMore !== false;
            this.hasMoreVendors = hasMoreFlag && receivedFullPage;
          } else if (Array.isArray(response.data)) {
            newVendors = response.data;
            this.totalVendorsCount = newVendors.length;
            this.hasMoreVendors = false;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            newVendors = response.data.data;
            this.totalVendorsCount = response.data.pagination?.total || newVendors.length;
            this.hasMoreVendors = false;
          }

          if (newVendors.length === 0) {
            this.hasMoreVendors = false;
            break;
          }

          newVendors = newVendors.map((vendor) => ({
            ...vendor,
            vendorName: vendor.vendorName || vendor.name || '',
            active: vendor.active !== undefined ? vendor.active : true,
          }));

          const existingIds = new Set(this.vendors.map((v) => v.id));
          const uniqueNewVendors = newVendors.filter((v) => v && v.id && !existingIds.has(v.id));
          this.vendors = [...this.vendors, ...uniqueNewVendors];

          logger.log(`Loaded ${uniqueNewVendors.length} vendors (page ${this.vendorsPage}). Total: ${this.vendors.length.toLocaleString()}/${this.totalVendorsCount.toLocaleString()}`);

          if (this.hasMoreVendors) {
            this.vendorsPage++;
            const delay = this.vendors.length > 10000 ? 200
              : this.vendors.length > 5000 ? 100
                : this.requestDelay;
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            break;
          }
        } catch (err) {
          if (err.name === 'AbortError' || err.name === 'CanceledError') {
            return;
          }

          consecutiveErrors++;
          logger.error(`Error loading vendors page ${this.vendorsPage} (attempt ${consecutiveErrors}/${maxConsecutiveErrors}):`, err);

          if (consecutiveErrors >= maxConsecutiveErrors) {
            logger.error('Too many consecutive errors. Stopping vendor load.');
            this.error = `Failed to load all vendors. Loaded ${this.vendors.length.toLocaleString()} vendors before error.`;
            this.hasMoreVendors = false;
            break;
          }

          const backoffDelay = this.retryDelay * Math.pow(2, consecutiveErrors - 1);
          logger.log(`Retrying after ${backoffDelay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
        } finally {
          this.loadingMoreVendors = false;
        }
      }

      if (pageCount >= maxPages) {
        logger.warn(`Reached maximum page limit (${maxPages}). Stopped loading.`);
        this.error = `Loaded ${this.vendors.length.toLocaleString()} vendors. Maximum limit reached.`;
      }

      logger.log(`Finished loading all vendors. Total: ${this.vendors.length.toLocaleString()}`);
    },
    async makeRequestWithRetry(url, config = {}, retryCount = 0) {
      this.activeRequests++;
      try {
        const response = await ApiService.get(url, config);
        return response;
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          throw err;
        }

        if (retryCount < this.maxRetries && (
          !err.response
          || (err.response.status >= 500 && err.response.status < 600)
        )) {
          const delay = this.retryDelay * Math.pow(2, retryCount);
          logger.log(`Request failed, retrying in ${delay}ms (attempt ${retryCount + 1}/${this.maxRetries})...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return this.makeRequestWithRetry(url, config, retryCount + 1);
        }

        throw err;
      } finally {
        this.activeRequests--;
      }
    },
    cancelPendingRequest() {
      if (this.currentRequestController) {
        this.currentRequestController.abort();
        this.currentRequestController = null;
      }
    },
    navigateToAddNewVendor() {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/vendors/new-vendor`);
    },
    exportToExcel() {
      this.showExportModal = false;
      this.performExcelExport();
    },
    async performExcelExport() {
      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        await exportVendorsToExcel({
          vendors: this.filteredVendors,
          companyId,
          currentView: this.currentView,
          getVendorMonthlyAmount: (vendorId, month) => this.getVendorMonthlyAmount(vendorId, month),
          getVendorYearToDate: (vendorId) => this.getVendorYearToDate(vendorId),
          showToast: (type, message) => {
            if (this.$toast) {
              this.$toast[type](message);
            }
          },
        });
      } catch (err) {
        logger.error('Error exporting vendors to Excel:', err);
      }
    },
    navigateToImport() {
      const companyId = this.$route.params.id || this.$route.params.companyId;
      this.$router.push({
        name: 'ImportFromFile',
        params: { id: companyId },
        query: { type: 'vendors', returnTo: 'vendors' },
      });
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
    },
    editVendor(vendorId) {
      if (!vendorId) return;
      const companyId = this.$route.params.id || this.$route.params.companyId;
      this.$router.push({
        name: 'EditVendor',
        params: {
          id: companyId,
          vendorId,
        },
      });
    },
    showVendorPreview(vendor) {
      if (!vendor) return;
      this.selectedVendor = vendor;
    },
    closePreview() {
      this.selectedVendor = null;
    },
    editSelectedVendor() {
      if (!this.selectedVendor) return;
      const vendorId = this.selectedVendor.id || this.selectedVendor.vendorId;
      if (!vendorId) {
        logger.error('Vendor ID not found');
        return;
      }
      this.closePreview();
      this.editVendor(vendorId);
    },
    calculateMonthlyData() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();

      logger.log('Calculating monthly data for', this.vendors.length, 'vendors with', this.bills.length, 'bills');

      this.vendors.forEach((vendor) => {
        const vendId = vendor.id || vendor.vendorId;

        const vendorBills = this.bills.filter((bill) => {
          const billVendorId = bill.vendorId || bill.vendor?.id || bill.vendorId;
          if (!billVendorId || !vendId) return false;
          return String(billVendorId) === String(vendId) || Number(billVendorId) === Number(vendId);
        });

        logger.log(`Vendor ${vendor.vendorName} (ID: ${vendId}) has ${vendorBills.length} bills`);

        vendor.monthlyData = {
          jan: 0,
          feb: 0,
          mar: 0,
          apr: 0,
          may: 0,
          jun: 0,
          jul: 0,
          aug: 0,
          sep: 0,
          oct: 0,
          nov: 0,
          dec: 0,
          ytd: 0,
        };

        vendorBills.forEach((bill) => {
          if (bill.status === 'Cancelled' || bill.status === 'Draft' || bill.status === 'cancelled' || bill.status === 'draft' || bill.voided === true) {
            return;
          }

          if (!bill.date) {
            logger.warn('Bill missing date:', bill);
            return;
          }

          const billDate = new Date(bill.date);

          if (isNaN(billDate.getTime())) {
            logger.warn('Invalid date for bill:', bill.date, bill);
            return;
          }

          const billMonth = billDate.getMonth();
          const billYear = billDate.getFullYear();

          const billAmount = parseFloat(bill.originalAmount)
            || parseFloat(bill.total)
            || parseFloat(bill.amount)
            || parseFloat(bill.totalAmount)
            || parseFloat(bill.billAmount)
            || 0;

          if (billAmount === 0 || isNaN(billAmount)) {
            logger.warn('Bill has zero or invalid amount:', bill);
            return;
          }

          if (billYear === currentYear) {
            const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            vendor.monthlyData[monthNames[billMonth]] += billAmount;
            logger.log(`Added ${billAmount} to ${monthNames[billMonth]} for vendor ${vendor.vendorName}`);
          }

          if (billYear === currentYear && billDate <= currentDate) {
            vendor.monthlyData.ytd += billAmount;
          }
        });

        logger.log(`Vendor ${vendor.vendorName} monthly totals:`, vendor.monthlyData);
      });
    },
    getVendorMonthlyAmount(vendorId, month) {
      const vendor = this.vendors.find((v) => {
        const vendId = v.id || v.vendorId;
        const searchId = vendorId;
        return vendId && searchId && String(vendId) === String(searchId);
      });

      if (!vendor || !vendor.monthlyData) {
        return 0;
      }

      const monthKey = month.toLowerCase();
      return vendor.monthlyData[monthKey] || 0;
    },
    getVendorYearToDate(vendorId) {
      const vendor = this.vendors.find((v) => {
        const vendId = v.id || v.vendorId;
        const searchId = vendorId;
        return vendId && searchId && String(vendId) === String(searchId);
      });

      if (!vendor || !vendor.monthlyData) {
        return 0;
      }

      return vendor.monthlyData.ytd || 0;
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount) || amount === 0) {
        return '-';
      }
      return '$' + Number(amount).toFixed(2);
    },
  },
  beforeUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.cancelPendingRequest();
    this.revokeAllVendorLogoUrls();
  },
};
</script>
